import type { Ref } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { computed, unref } from 'vue'
import { getApi } from './api'

interface GetAccountContainerTypesParams {
  page?: number
  per_page?: number
  [key: string]: any
}

/**
 * The account's container-type vocabulary, in the standard paginated envelope
 * (`data` / `links` / `meta`, `per_page` 25 by default). Any member of the account may read it;
 * only its admins may write.
 *
 * The whole cluster answers `404` while `returnable_containers_available` is off — the feature is
 * hidden, not forbidden. Both apps route a 404 away from the page (admin replaces it with the
 * not-found view, the customer app redirects), so this is survivable on both sides: `allow404`
 * for admin's interceptor, and the URL is whitelisted in the customer app's. An account that
 * loses the flag, or an engine that does not have the routes yet, then reads as an empty
 * vocabulary instead of taking the page with it.
 */
export default (
  accountId: Ref<number> | number,
  params?: Ref<GetAccountContainerTypesParams> | GetAccountContainerTypesParams,
  options?: { enabled?: Ref<boolean> | boolean },
) => {
  return useQuery({
    queryKey: [
      'account-container-types',
      () => unref(accountId),
      () => unref(params),
    ],
    queryFn: async () => {
      const { api, baseURL } = getApi()
      const res = await api.get(
        `/api/v2/accounts/${unref(accountId)}/container-types`,
        { baseURL, params: unref(params) || {}, allow404: true },
      )
      return res?.data ?? null
    },
    // A caller that only knows later whether the account is entitled passes `enabled` rather
    // than spending a request to be told 404.
    enabled: computed(
      () =>
        !!unref(accountId) &&
        (options?.enabled === undefined || !!unref(options.enabled)),
    ),
  })
}
